// frontend/contexts/PersonaContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useActualApi } from '../hooks/useActualApi';
import { useAuth } from './AuthContext';

interface PersonaContextType {
  personaName: string | null;
  isLoadingPersona: boolean;
  refreshPersona: () => Promise<void>;
}

const PersonaContext = createContext<PersonaContextType | undefined>(undefined);

export const PersonaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { idToken } = useAuth();
  const api = useActualApi();
  
  const [personaName, setPersonaName] = useState<string | null>(null);
  const [isLoadingPersona, setIsLoadingPersona] = useState(false);

  const loadPersona = async () => {
    if (!idToken) {
      setPersonaName(null);
      return;
    }

    setIsLoadingPersona(true);
    try {
      const resp = await api.fetchAiPersona();
      if (resp && resp.success && resp.persona) {
        setPersonaName(resp.persona.personaName || null);
      } else {
        setPersonaName(null);
      }
    } catch (e: any) {
      console.error('Failed to fetch persona name', e);
      setPersonaName(null);
    } finally {
      setIsLoadingPersona(false);
    }
  };

  // Load persona when auth changes
  useEffect(() => {
    if (idToken) {
      loadPersona();
    } else {
      setPersonaName(null);
    }
  }, [idToken]);

  const refreshPersona = async () => {
    await loadPersona();
  };

  return (
    <PersonaContext.Provider value={{ personaName, isLoadingPersona, refreshPersona }}>
      {children}
    </PersonaContext.Provider>
  );
};

export const usePersona = (): PersonaContextType => {
  const context = useContext(PersonaContext);
  if (context === undefined) {
    throw new Error('usePersona must be used within a PersonaProvider');
  }
  return context;
};
